import {
	type InvitedAppShortcutAllowedTargetAppKey,
	type InvitedAppShortcutRecommendedAppInput,
} from '../../../common/types';
import {
	type ExperimentDetails,
	IneligibilityReason,
	type IneligibleExperimentDetails,
	type InvitedAppDetails,
} from '../types';

export type ResolvedCacheDetails = {
	eligibility: ExperimentDetails;
};

/**
 * Build a `ResolvedCacheDetails` from a caller-supplied list of recommended target apps (derived
 * from `InvitedAppShortcutsRecommendationsProvider`). Returns `NO_RECOMMENDED_APPS` when
 * `recommendedApps` is empty.
 *
 * Notes:
 * - Only recommended apps appear in `invitedApps`; non-recommended target products are absent.
 *   Consumers must iterate `targetProducts` themselves when they need to enumerate all possible
 *   apps.
 * - `collaborators` / `firstImpressionAt` are intentionally left `undefined`; collaborators are
 *   owned by Relay and impression state by GRS. See `InvitedAppDetails`.
 * - `invitedApps` is keyed by the **parsed product**, never by the raw GRS `entityId`, which since
 *   PUMA-2245 is variant-encoded.
 */
export const resolveFromRecommendations = ({
	recommendedApps,
}: {
	recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedAppInput>;
}): ResolvedCacheDetails => {
	if (recommendedApps.length === 0) {
		return { eligibility: ineligible(IneligibilityReason.NO_RECOMMENDED_APPS) };
	}

	const invitedApps: Partial<Record<InvitedAppShortcutAllowedTargetAppKey, InvitedAppDetails>> = {};
	for (const { product, variant } of recommendedApps) {
		invitedApps[product] = { eligible: true, variant };
	}

	return { eligibility: { ineligibilityReason: null, invitedApps } };
};

export const ineligible = (reason: IneligibilityReason): IneligibleExperimentDetails => ({
	ineligibilityReason: reason,
});
